<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte'
    const dispatch = createEventDispatcher()
    import { faAngleLeft, faEdit, faFileImport, faPlusCircle, faSave, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
    import Content from '../../_common/content.svelte'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import { Service } from '../../../modules/posApi/services/service'
    import { ServiceConsumptionItem } from '../../../modules/posApi/services/service-consumption-item'
    import type { CreateServiceDto } from '../../../modules/posApi/services/dto/create-service.dto'
    import type { UpdateServiceDto } from '../../../modules/posApi/services/dto/update-service.dto'
    import dayjs from 'dayjs'
    import { GridButton, GridClass, GridCol, GridColTypeEnum, GridLine } from '../../_common/z/zgrid'
    import Zgrid from '../../_common/z/zgrid.svelte'
    import Zframe from '../../_common/z/zframe.svelte'
    import Zact from '../../_common/z/zact.svelte'
    import Zbutton from '../../_common/z/zbutton.svelte'
    import Zinput from '../../_common/z/zinput.svelte'
    import ZContainer from '../../_common/z/z-container.svelte'
    import ZContainerCol from '../../_common/z/z-container-col.svelte'
    import type { Schedule } from '../../../modules/schedulerApi/schedules/schedule'

    import Zexpand from '../../_common/z/zexpand.svelte'
    import AudienceFileImport from '../../automacao/audience/audience-file-import.svelte'
    import type { FormTemplate } from '../../../modules/toolsApi/forms/form-templates/form-template'
    import type { FormV2Template } from '../../../modules/toolsApi/forms-v2/form-v2-templates/form-v2-template'
    import ZentitySelect from '../../_common/z/zentity-select.svelte'

    // ##############################################################################################################

    const entityId = 'serviceId'

    export let form: boolean = false
    export let serviceId: string = null
    let entity: Service = new Service()

    let csvData = []
    let csvTypes = []
    csvTypes.push({ value: 'name', label: 'Nome do serviço' })
    csvTypes.push({ value: 'duration', label: 'Duração' })
    csvTypes.push({ value: 'value', label: 'Valor' })
    csvTypes.push({ value: 'comissionPercentage', label: 'Comissão do vendedor %' })
    csvTypes.push({ value: 'comissionPercentageAttendant', label: 'Comissão do atendente %' })
    csvTypes.push({ value: '-', label: 'Outro / Não importar' })
    let validImport = false

    async function editEntity(id: string) {
        try {
            show = true
            await Promise.all([loadSchedulers(), loadDocuments()])
            entity = new Service()
            entity = await window.api.pos.services.getOne(id, { category: true })
            if (!entity.schedulers) entity.schedulers = []
            if (!entity.schedulersOnline) entity.schedulersOnline = []
            schedulersAll = entity.schedulers.length === 0
            schedulersOnlineAll = entity.schedulersOnline.length === 0
            documentsAll = entity.documents.length === 0
            if (!entity.consumptionRecipe) entity.consumptionRecipe = []
            for (const item of entity.consumptionRecipe) {
                // só o id — o próprio ServiceSelect_2 busca os detalhes completos (ver seu $: loadService)
                item.consumable = { serviceId: item.consumableServiceId } as Service
            }
            console.log('service', entity)
            step = 2
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    // Transição v1 → v2: os modelos novos entram no mesmo multiselect com um prefixo no value
    // ("v2:<id>"), pra diferenciar de qual versão buscar o histórico de emissão de cada um (ver
    // checkRequiredDocument em appointment-form.svelte). Nenhuma migração de dados já configurados
    // é necessária — os dois formatos convivem em "entity.documents".
    const V2_DOCUMENT_PREFIX = 'v2:'

    let documentsAll = true
    let documents: FormTemplate[] = []
    let documentsV2: FormV2Template[] = []
    async function loadDocuments() {
        const [v1, v2] = await Promise.all([window.api.tools.forms.formTemplates.getAll(), window.api.tools.formsV2.formTemplates.getAll()])
        documents = v1.filter((el) => el.status >= 70)
        documentsV2 = v2.filter((el) => el.status >= 70)
    }
    $: documentOptions = [
        ...documents.map((el) => ({ value: el.formTemplateId, label: el.title })),
        ...documentsV2.map((el) => ({ value: V2_DOCUMENT_PREFIX + el.formV2TemplateId, label: el.title + ' (novo)' })),
    ]

    let schedulersAll = true
    let schedulersOnlineAll = true

    let schedulers: Schedule[] = []
    async function loadSchedulers() {
        schedulers = await window.api.schedulers.schedules.getAll({ all: true })
    }
    $: schedulerOptions = schedulers.map((el) => ({ value: el.scheduleId, label: el.label }))
    $: schedulerOnlineOptions = schedulers.filter((el) => el.public).map((el) => ({ value: el.scheduleId, label: el.label }))

    const stockTypeOptions = [
        { value: 'quantity', label: 'Quantidade' },
        { value: 'minute', label: 'Minuto' },
        { value: 'hour', label: 'Hora' },
    ]

    async function newEntity() {
        await Promise.all([loadSchedulers(), loadDocuments()])

        entity = new Service()
        entity.balance = true
        entity.public = true
        entity.scheduleInterval = 0
        entity.schedulers = []
        entity.schedulersOnline = []
        entity.stockType = 'quantity'
        schedulersAll = true
        schedulersOnlineAll = true
        entity.documents = []
        documentsAll = true
        entity.generatesConsumption = false
        entity.consumptionRecipe = []

        step = 2
    }

    function addRecipeItem() {
        entity.consumptionRecipe = [...(entity.consumptionRecipe || []), new ServiceConsumptionItem()]
    }

    function removeRecipeItem(index: number) {
        entity.consumptionRecipe = entity.consumptionRecipe.filter((_, i) => i !== index)
    }

    function buildConsumptionRecipeDto() {
        return (entity.consumptionRecipe || [])
            .filter((el) => el.consumable?.serviceId)
            .map((el) => ({
                serviceConsumptionItemId: el.serviceConsumptionItemId,
                consumableServiceId: el.consumable.serviceId,
                quantity: el.quantity,
            }))
    }

    async function createEntity(returnToStep1 = true, blockDuplicity = false) {
        try {
            show = true
            if (schedulersAll === true) entity.schedulers = []
            if (schedulersOnlineAll === true) entity.schedulersOnline = []
            if (documentsAll === true) entity.documents = []
            if (entity.duration && entity.duration > 999) entity.duration = 999
            const dto: CreateServiceDto = {
                consulting: entity.consulting,
                comissionFixed: entity.comissionFixed || 0,
                comissionPercentage: entity.comissionPercentage || 0,
                comissionCosts: entity.comissionCosts || 0,
                comissionFixedAttendant: entity.comissionFixedAttendant || 0,
                comissionPercentageAttendant: entity.comissionPercentageAttendant || 0,
                comissionCostsAttendant: entity.comissionCostsAttendant || 0,
                duration: entity.duration ? parseInt(entity.duration.toString()) : 0,
                name: entity.name,
                price: entity.price ? parseFloat(entity.price.toString()) : 0,
                costs: entity.costs || 0,
                balance: entity.balance,
                type: 'service',
                stock: 0,
                public: entity.public,
                scheduleInterval: entity.scheduleInterval,
                schedulers: entity.schedulers,
                schedulersOnline: entity.schedulersOnline,
                documents: entity.documents,
                description: entity.description,
                needResource: entity.needResource,
                generatesConsumption: entity.generatesConsumption,
                expirationDays: undefined,
                blockDuplicity: blockDuplicity,
                serviceCategoryId: entity.category?.serviceCategoryId || entity.serviceCategoryId,
                stockType: entity.stockType,
                consumptionRecipe: buildConsumptionRecipeDto(),
            }
            entity = await window.api.pos.services.create(dto)
            if (form) {
                sendClose()
                return
            }
            if (returnToStep1) {
                await loadEntities()
            }
        } catch (error) {
            evaluateError(error)
        } finally {
            if (returnToStep1) {
                show = false
            }
        }
    }

    function sendClose() {
        dispatch('close')
    }

    async function updateEntity() {
        try {
            show = true
            if (schedulersAll === true) entity.schedulers = []
            if (schedulersOnlineAll === true) entity.schedulersOnline = []
            if (documentsAll === true) entity.documents = []
            if (entity.duration && entity.duration > 999) entity.duration = 999
            const dto: UpdateServiceDto = {
                comissionFixed: entity.comissionFixed || 0,
                comissionPercentage: entity.comissionPercentage || 0,
                comissionCosts: entity.comissionCosts || 0,
                comissionFixedAttendant: entity.comissionFixedAttendant || 0,
                comissionPercentageAttendant: entity.comissionPercentageAttendant || 0,
                comissionCostsAttendant: entity.comissionCostsAttendant || 0,
                duration: entity.duration,
                name: entity.name,
                price: entity.price,
                consulting: entity.consulting,
                costs: entity.costs,
                balance: entity.balance,
                public: entity.public,
                scheduleInterval: entity.scheduleInterval,
                schedulers: entity.schedulers,
                schedulersOnline: entity.schedulersOnline,
                documents: entity.documents,
                description: entity.description,
                needResource: entity.needResource,
                generatesConsumption: entity.generatesConsumption,
                serviceCategoryId: entity.category?.serviceCategoryId || entity.serviceCategoryId,
                stockType: entity.stockType,
                consumptionRecipe: buildConsumptionRecipeDto(),
            }
            await window.api.pos.services.update(entity[entityId], dto)
            if (form) {
                sendClose()
                return
            }
            await loadEntities()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function saveEntity() {
        if (entity[entityId]) {
            await updateEntity()
        } else {
            await createEntity(undefined, true)
        }
    }

    async function loadEntities() {
        try {
            show = true
            step = 1
            entities = await window.api.pos.services.getAll(undefined, 60)
            entities.map((el) => {
                if (!el.schedulers) el.schedulers = []
                if (!el.schedulersOnline) el.schedulersOnline = []
                if (!el.documents) el.documents = []
            })
            loadGrid()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    let grid: GridClass = new GridClass()
    function loadGrid() {
        // grid
        grid = new GridClass()
        // grid head
        grid.title = 'Serviços'
        grid.subtitle = ''
        grid.cols.push(new GridCol({ content: 'Serviço' }))
        grid.cols.push(new GridCol({ content: 'ID' }))
        grid.cols.push(new GridCol({ content: 'Alteração', type: GridColTypeEnum.datetime }))
        grid.cols.push(new GridCol({ content: 'Duração' }))
        grid.cols.push(new GridCol({ content: 'Valor', size: 1, type: GridColTypeEnum.float_2 }))
        grid.cols.push(new GridCol({ content: 'Com. vend.', size: 1, type: GridColTypeEnum.float_2 }))
        grid.cols.push(new GridCol({ content: 'Com. atend.', size: 1, type: GridColTypeEnum.float_2 }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        // grid lines
        for (const item of entities) {
            const btnPerson = new GridButton({
                callback: () => editEntity(item.serviceId),
                icon: faEdit,
                label: 'Editar',
            })
            let line: GridLine = new GridLine()
            line.slots.push({ content: item.name })
            line.slots.push({ content: item.serviceId })
            line.slots.push({ content: item.updatedAt })
            line.slots.push({ content: dayjs(new Date(0, 0, 0, 0, item.duration, 0)).format('HH:mm') })
            line.slots.push({ content: item.price })
            line.slots.push({ content: item.comissionPercentage })
            line.slots.push({ content: item.comissionPercentageAttendant })
            line.slots.push({ content: [btnPerson] })
            grid.lines.push(line)
        }
    }

    async function removeEntity() {
        if (!confirm('Deseja mesmo excluir este registro?')) {
            return
        }
        try {
            show = true
            await window.api.pos.services.remove(entity[entityId])
            await loadEntities()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function evaluateError(error) {
        window.error(error)
    }

    function showEntities() {
        step = 1
    }

    let show = false
    let step = 1
    let entities: Service[] = []
    let expand = true
    onMount(async () => {
        await loadEntities()
        if (form) {
            expand = false
            if (serviceId) {
                await editEntity(serviceId)
            } else {
                await newEntity()
            }
        }
    })

    function importFile() {
        step = 3
    }

    async function saveImport() {
        if (!validImport) {
            window.error('Dados inválidos. Informe todos os campos.')
            return
        }
        console.log('loaded data', csvData)
        let success = 0
        for (const data of csvData) {
            if (!data['name']) continue
            let duration = 60
            if (data['duration']) {
                if (data['duration'].includes(':')) {
                    let segments = data['duration'].split(':')
                    duration = parseInt(segments[0]) * 60 + parseInt(segments[1])
                } else {
                    duration = data['duration']
                }
            }
            let price = data['value'] || '0'
            price = parseFloat(price?.replaceAll('R$', '').trim())
            entity = new Service()
            entity.consulting = false
            entity.comissionFixed = 0
            entity.comissionPercentage = parseFloat(data['comissionPercentage']) || 0
            entity.comissionCosts = 0
            entity.comissionFixedAttendant = 0
            entity.comissionPercentageAttendant = parseFloat(data['comissionPercentageAttendant']) || 0
            entity.comissionCostsAttendant = 0
            entity.duration = duration
            entity.name = data['name']
            entity.price = price
            entity.costs = 0
            entity.balance = true
            entity.type = 'service'
            entity.stock = 0
            entity.public = true
            entity.scheduleInterval = 0
            entity.schedulers = []
            entity.schedulersOnline = []
            entity.documents = []
            entity.description = ''
            entity.status = 100
            entity.needResource = false
            entity.stockType = 'quantity'
            await createEntity(false, true)
            success++
        }
        await loadEntities()
        window.alert('Importação de ' + success + ' serviços concluída.')
    }
</script>

<LoadSpinner {show} />
{#if !show}
    {#if step === 1}
        <Content footer={true}>
            <Zact>
                <div class="spacer" />
                {#if window.store.token.permissions.tools.services.write}
                    <Zbutton color="secondary" icon={faFileImport} label="Importar" on:click={importFile} />
                    <Zbutton color="primary" icon={faPlusCircle} label="Novo serviço" mobileHideLabel={true} on:click={newEntity} />
                {/if}
            </Zact>
            <Zgrid class="h-space mb-7" {grid} />
        </Content>
    {/if}
    {#if step === 3}
        <Content footer={true}>
            <Zact>
                {#if !form}
                    <Zbutton color="secondary" icon={faAngleLeft} label="Voltar" on:click={showEntities} />
                {/if}
                <div class="spacer" />
                {#if window.store.token.permissions.tools.services.write}
                    <Zbutton color="primary" icon={faSave} label="Importar" loading={show} disabled={show} on:click={saveImport} />
                {/if}
            </Zact>

            <div class="px-3">
                <Zframe class="h-space" title="Importação">
                    <AudienceFileImport
                        types={csvTypes}
                        bind:data={csvData}
                        bind:valid={validImport}
                        instructions="Selecione um arquivo XLSX ou CSV com os dados a serem importados."
                    />
                </Zframe>
            </div>
        </Content>
    {/if}
    {#if step === 2}
        <Content footer={true}>
            <Zact>
                {#if !form}
                    <Zbutton color="secondary" icon={faAngleLeft} label="Voltar" on:click={showEntities} />
                {/if}
                <div class="spacer" />
                {#if window.store.token.permissions.tools.services.write}
                    {#if !!entity[entityId]}
                        <Zbutton color="red" icon={faTrashAlt} label="Excluir" disabled={show} on:click={removeEntity} />
                    {/if}
                    <Zbutton color="primary" icon={faSave} label="Salvar" loading={show} disabled={show} on:click={saveEntity} />
                {/if}
            </Zact>

            <Zframe class="h-space" title="Serviço">
                <ZContainer>
                    <Zinput size={4} sizeM={12} label="Nome" bind:value={entity.name} />
                    <Zinput size={8} sizeM={12} label="Descrição" bind:value={entity.description} />
                    <Zinput size={4} sizeM={12} type="number" label="Duração minutos" bind:value={entity.duration} />
                    <Zinput size={4} sizeM={12} type="number" label="Valor {window.configs.currency}" bind:value={entity.price} />
                    <ZContainerCol size={4} sizeM={12}>
                        <ZentitySelect fonte="categoriaServico" label="Categoria" bind:value={entity.category} />
                    </ZContainerCol>
                </ZContainer>
            </Zframe>

            <Zexpand {expand} class="mt-3 mb-5">
                <Zframe class="" title="Configurações">
                    <ZContainer>
                        <Zinput size={6} sizeM={12} type="toggle" label="Este item é uma avaliação/reunião/orçamento" bind:value={entity.consulting} />
                        <Zinput
                            size={6}
                            sizeM={12}
                            type="toggle"
                            label="Exigir o bloqueio de uma sala/aparelho ao agendar este serviço"
                            bind:value={entity.needResource}
                        />
                        <Zinput size={6} sizeM={12} type="toggle" label="Controlar saldo de uso deste serviço pelo cliente" bind:value={entity.balance} />
                        <Zinput size={6} sizeM={12} type="select" label="Controlar o saldo por" options={stockTypeOptions} bind:value={entity.stockType} />
                        {#if entity.stockType !== 'quantity'}
                            <ZContainerCol>
                                <div class="alert alert-primary">
                                    Controle por tempo: a duração do agendamento será dividida entre todos os serviços presentes no agendamento que possuam
                                    controle por tempo selecionado. Serviços que não possuem controle por tempo serão desconsiderados nessa divisão.<br />
                                    No controle por hora os agendamentos fracionados serão arredondados para cima (ex.: 20 minutos serão contabilizados como 1 hora).
                                    Caso precise de um controle mais preciso escolha a opção "minuto".
                                </div>
                            </ZContainerCol>
                        {/if}
                    </ZContainer>
                </Zframe>
                <Zframe
                    class="mt-3"
                    title="Consumo de insumos"
                    headline="Insumos e quantidades consumidos automaticamente quando este serviço é realizado. O atendente pode ajustar na hora do agendamento."
                >
                    <ZContainer>
                        <Zinput size={12} sizeM={12} type="toggle" label="Este serviço gera consumo de insumos" bind:value={entity.generatesConsumption} />
                        {#if entity.generatesConsumption}
                            {#each entity.consumptionRecipe as item, index}
                                <ZContainerCol size={6} sizeM={8}>
                                    <ZentitySelect fonte="servico" label="Insumo" params={{ type: 'consumable' }} bind:value={item.consumable} />
                                </ZContainerCol>
                                <ZContainerCol size={3} sizeM={3}>
                                    <Zinput type="number" label="Quantidade" bind:value={item.quantity} />
                                </ZContainerCol>
                                <ZContainerCol size={3} sizeM={1} verticalAlign="end">
                                    <Zbutton color="red" icon={faTrashAlt} on:click={() => removeRecipeItem(index)} />
                                </ZContainerCol>
                            {/each}
                            <ZContainerCol size={2} sizeM={12}>
                                <Zbutton color="secondary" icon={faPlusCircle} label="Adicionar insumo" on:click={addRecipeItem} />
                            </ZContainerCol>
                        {/if}
                    </ZContainer>
                </Zframe>
                <Zframe class="mt-3" title="Comissões">
                    <ZContainer>
                        <ZContainerCol size={12}>Comissões do Vendedor</ZContainerCol>
                        <Zinput size={4} sizeM={12} type="number" label="Comissão %" bind:value={entity.comissionPercentage} />
                        <Zinput size={4} sizeM={12} type="number" label="Comissão Fixa ({window.configs.currency})" bind:value={entity.comissionFixed} />
                        <Zinput size={4} sizeM={12} type="number" label="Outros custos a descontar" bind:value={entity.comissionCosts} />

                        <ZContainerCol size={12}>Comissões do Atendente</ZContainerCol>
                        <Zinput size={4} sizeM={12} type="number" label="Comissão %" bind:value={entity.comissionPercentageAttendant} />
                        <Zinput
                            size={4}
                            sizeM={12}
                            type="number"
                            label="Comissão Fixa ({window.configs.currency})"
                            bind:value={entity.comissionFixedAttendant}
                        />
                        <Zinput size={4} sizeM={12} type="number" label="Outros custos a descontar" bind:value={entity.comissionCostsAttendant} />
                    </ZContainer>
                </Zframe>
                <Zframe
                    title="Documentos obrigatórios para agendamento"
                    headline="Você pode restringir um agendamento caso um determinado documento não tenha sido emitido ao cliente"
                    class="mt-3"
                >
                    <ZContainer>
                        <Zinput type="toggle" label="Permitir agendar sem emissão de documentos" bind:value={documentsAll} />
                        {#if !documentsAll}
                            <Zinput type="multiselect" size={6} sizeM={12} label="Documentos" options={documentOptions} bind:value={entity.documents} />
                        {/if}
                    </ZContainer>
                </Zframe>
                <Zframe title="Agendas autorizadas" headline="Você pode escolher em qual agenda este serviço poderá ser agendado" class="mt-3">
                    <ZContainer>
                        <Zinput type="toggle" label="Permitir agendar em todas as agendas" bind:value={schedulersAll} />
                        {#if !schedulersAll}
                            <Zinput type="multiselect" size={6} sizeM={12} label="Agendas" options={schedulerOptions} bind:value={entity.schedulers} />
                        {/if}
                    </ZContainer>
                </Zframe>
                <Zframe title="Agendamento Online" class="mt-3">
                    <ZContainer>
                        <Zinput type="toggle" size={12} label="Permitir em agendamentos online" bind:value={entity.public} />
                        {#if entity.public}
                            <Zinput
                                size={4}
                                type="number"
                                label="Intervalo entre agendamentos (dias)"
                                bind:value={entity.scheduleInterval}
                                help="Caso seja obrigatório aguardar um intervalo de dias entre um agendamento e outro, informe este intervalo aqui. Caso contrário informe 0."
                            />
                            <Zinput size={4} type="toggle" label="Permitir agendar em todas as agendas" bind:value={schedulersOnlineAll} />
                            {#if !schedulersOnlineAll}
                                <Zinput
                                    size={4}
                                    type="multiselect"
                                    label="Agendas"
                                    options={schedulerOnlineOptions}
                                    bind:value={entity.schedulersOnline}
                                    help="Caso a agenda não esteja na lista verifique se a mesma está ativa para agendamentos online."
                                />
                            {/if}
                        {/if}
                    </ZContainer>
                </Zframe>
            </Zexpand>
        </Content>
    {/if}
{/if}
